import { Search, RefreshCw, ArrowUpDown, Loader2 } from 'lucide-react';
import { RegionCoverage } from './RegionCoverage';

export function FeedMonitorTab({ feedData, loading, loadFeedMonitor, feedSearch, setFeedSearch, feedCategoryFilter, setFeedCategoryFilter, feedTypeFilter, setFeedTypeFilter, feedStatusFilter, setFeedStatusFilter, feedSort, setFeedSort }) {
  const ok = feedData.filter(f => f.status === 'ok').length;
  const warn = feedData.filter(f => f.status === 'warning').length;
  const err = feedData.filter(f => f.status === 'error').length;

  const filteredFeeds = feedData
    .filter(f => {
      if (feedCategoryFilter !== 'all' && f.category !== feedCategoryFilter) return false;
      if (feedTypeFilter !== 'all' && f.type !== feedTypeFilter) return false;
      if (feedStatusFilter !== 'all' && f.status !== feedStatusFilter) return false;
      if (feedSearch && !f.label.toLowerCase().includes(feedSearch.toLowerCase()) && !f.source.toLowerCase().includes(feedSearch.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const key = feedSort.key;
      const dir = feedSort.dir === 'asc' ? 1 : -1;
      if (typeof a[key] === 'number') return (a[key] - b[key]) * dir;
      return String(a[key] || '').localeCompare(String(b[key] || '')) * dir;
    });

  const CATEGORY_STYLES = {
    crypto: { bg: '#FFF7ED', color: '#EA580C' }, technology: { bg: '#EFF6FF', color: '#2563EB' },
    finance: { bg: '#ECFDF5', color: '#059669' }, world_news: { bg: '#FEF2F2', color: '#DC2626' },
    entertainment: { bg: '#FDF2F8', color: '#DB2777' }, sports: { bg: '#F0FDF4', color: '#16A34A' },
  };

  return (
    <div className="mt-2" data-testid="feed-monitor">
      {/* Region Coverage Health — moved to top of Feed Monitor April 2026 */}
      <div className="mb-4">
        <RegionCoverage />
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900">Data Feed Monitor</h2>
        <button onClick={loadFeedMonitor} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors disabled:opacity-50">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="feed-search" type="text" placeholder="Search source..."
            value={feedSearch} onChange={e => setFeedSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-gray-300" />
        </div>
        <select data-testid="feed-filter-category" value={feedCategoryFilter} onChange={e => setFeedCategoryFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-xs bg-white focus:outline-none">
          <option value="all">All Categories</option>
          {[...new Set(feedData.map(f => f.category))].sort().map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="feed-filter-type" value={feedTypeFilter} onChange={e => setFeedTypeFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-xs bg-white focus:outline-none">
          <option value="all">All Types</option>
          {[...new Set(feedData.map(f => f.type))].sort().map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="feed-filter-status" value={feedStatusFilter} onChange={e => setFeedStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-xs bg-white focus:outline-none">
          <option value="all">All Status</option>
          <option value="ok">OK</option>
          <option value="warning">Warning</option>
          <option value="error">Error</option>
        </select>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1 bg-green-50 rounded-xl p-3 text-center">
          <p className="text-[10px] font-bold text-green-600 tracking-widest uppercase">Active</p>
          <p className="text-xl font-extrabold text-green-700">{ok}</p>
        </div>
        <div className="flex-1 bg-amber-50 rounded-xl p-3 text-center">
          <p className="text-[10px] font-bold text-amber-600 tracking-widest uppercase">Warning</p>
          <p className="text-xl font-extrabold text-amber-700">{warn}</p>
        </div>
        <div className="flex-1 bg-red-50 rounded-xl p-3 text-center">
          <p className="text-[10px] font-bold text-red-600 tracking-widest uppercase">Error</p>
          <p className="text-xl font-extrabold text-red-700">{err}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {[
                  { key: 'category', label: 'Category' }, { key: 'label', label: 'Source' },
                  { key: 'type', label: 'Type' }, { key: 'records_2d', label: 'Records (2d)' },
                  { key: 'last_run', label: 'Last Run' }, { key: 'issues', label: 'Issues' },
                ].map(col => (
                  <th key={col.key}
                    onClick={() => setFeedSort(prev => ({ key: col.key, dir: prev.key === col.key && prev.dir === 'asc' ? 'desc' : 'asc' }))}
                    className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap">
                    <span className="flex items-center gap-1">
                      {col.label}
                      <ArrowUpDown size={10} className={feedSort.key === col.key ? 'text-gray-900' : 'text-gray-300'} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredFeeds.map((feed, i) => {
                const catStyle = CATEGORY_STYLES[feed.category] || { bg: '#F3F4F6', color: '#6B7280' };
                return (
                  <tr key={feed.source} data-testid={`feed-row-${feed.source}`}
                    className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase"
                        style={{ background: catStyle.bg, color: catStyle.color }}>{feed.category}</span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{feed.label}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${feed.type === 'RSS' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{feed.type}</span>
                    </td>
                    <td className="px-4 py-3 font-mono font-bold text-gray-900">{feed.records_2d}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {feed.last_run && feed.last_run !== 'Never'
                        ? new Date(feed.last_run).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                        : <span className="text-red-500 font-semibold">Never</span>}
                    </td>
                    <td className="px-4 py-3">
                      {feed.issues === 'None'
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold">OK</span>
                        : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold max-w-[200px] truncate" title={feed.issues}>{feed.issues}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {loading && <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-gray-400" /></div>}
      </div>
    </div>
  );
}
